import {
  Boxes,
  ClipboardList,
  FileStack,
  FileText,
  FolderTree,
  GraduationCap,
  Image,
  LayoutDashboard,
  ListTree,
  Newspaper,
  Package,
  Plane,
  ScrollText,
  Settings,
  ShieldCheck,
  Tags,
  Users,
  UserSquare,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { CERTIFICATE_ROLES, CONTENT_ROLES, REQUEST_ROLES, type Role } from '@geoic/shared';

export type NavItem = {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Qaysi rollar ko'radi. */
  roles: readonly Role[];
  /** Bo'lim hali qurilmagan — bosqich raqami ko'rsatiladi. */
  stage?: 'B9';
};

export type NavGroup = { title: string; items: readonly NavItem[] };

const ALL_ROLES: readonly Role[] = ['superadmin', 'admin', 'editor', 'sales'];
const ADMIN_ROLES: readonly Role[] = ['superadmin', 'admin'];

/**
 * Yon menyu — 18 band (D0).
 *
 * Har bandda `roles` bor va menyu shu ro'yxat bo'yicha filtrlanadi.
 * `sales` faqat «Arizalar» ni ko'radi (TZ 3.2) — bu B6 DoD talabi.
 *
 * MUHIM: menyuni yashirish HIMOYA EMAS. Haqiqiy tekshiruv serverdagi
 * `RolesGuard` da; bu yerda faqat foydalanuvchi bosa olmaydigan bandlarni
 * ko'rsatmaslik.
 */
export const NAV_GROUPS: readonly NavGroup[] = [
  {
    title: 'Asosiy',
    items: [
      { path: '/', label: 'Boshqaruv paneli', icon: LayoutDashboard, roles: ALL_ROLES },
      { path: '/requests', label: 'Arizalar', icon: ClipboardList, roles: REQUEST_ROLES },
      // Sertifikat — rasmiy hujjat: `editor` va `sales` ko'rmaydi ham
      {
        path: '/certificates',
        label: 'Sertifikatlar',
        icon: ShieldCheck,
        roles: CERTIFICATE_ROLES,
      },
    ],
  },
  {
    title: 'Kontent',
    items: [
      { path: '/pages', label: 'Sahifalar', icon: FileStack, roles: CONTENT_ROLES },
      { path: '/news', label: 'Yangiliklar', icon: Newspaper, roles: CONTENT_ROLES },
      { path: '/services', label: 'Xizmatlar', icon: Wrench, roles: CONTENT_ROLES },
      { path: '/courses', label: 'Kurslar', icon: GraduationCap, roles: CONTENT_ROLES },
      { path: '/projects', label: 'Loyihalar', icon: Boxes, roles: CONTENT_ROLES },
      // Dron-shou ikki ekrandan iborat (paketlar + portfolio), lekin menyuda
      // BITTA band: kategoriyalar kabi ikkinchi darajali ekranlar yon menyuni
      // uzaytirib, asosiy bo'limlarni ko'rinmas qilib qo'yardi (D0 — 18 band).
      { path: '/droneshow', label: 'Dron-shou', icon: Plane, roles: CONTENT_ROLES },
      { path: '/staff', label: 'Rahbariyat', icon: UserSquare, roles: CONTENT_ROLES },
      { path: '/documents', label: 'Hujjatlar', icon: FileText, roles: CONTENT_ROLES },
      { path: '/menus', label: 'Menyu', icon: ListTree, roles: CONTENT_ROLES },
    ],
  },
  {
    title: 'Do‘kon',
    items: [
      { path: '/products', label: 'Mahsulotlar', icon: Package, roles: CONTENT_ROLES },
      { path: '/categories', label: 'Kategoriyalar', icon: FolderTree, roles: CONTENT_ROLES },
      { path: '/prices', label: 'Narxlar', icon: Tags, roles: ADMIN_ROLES },
    ],
  },
  {
    title: 'Tizim',
    items: [
      { path: '/media', label: 'Media', icon: Image, roles: CONTENT_ROLES },
      { path: '/settings', label: 'Sozlamalar', icon: Settings, roles: ADMIN_ROLES },
      { path: '/users', label: 'Foydalanuvchilar', icon: Users, roles: ['superadmin'] },
      { path: '/audit', label: 'Jurnal', icon: ScrollText, roles: ADMIN_ROLES },
    ],
  },
];

/** Barcha bandlar — testlar va yo'llarni tekshirish uchun. */
export const NAV_ITEMS: readonly NavItem[] = NAV_GROUPS.flatMap((group) => group.items);

export function visibleGroups(role: Role | undefined): NavGroup[] {
  if (!role) return [];

  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => item.roles.includes(role)),
  })).filter((group) => group.items.length > 0);
}

/** Foydalanuvchi kirgach qaysi sahifaga tushadi. */
export function landingPath(role: Role | undefined): string {
  // `sales` uchun boshqaruv paneli ham ochiq, lekin uning ishi arizalarda —
  // har safar bir bosish tejaladi.
  return role === 'sales' ? '/requests' : '/';
}
